/**
 * What a country implies.
 *
 * Three of the four decisions that cannot be retrofitted are derived here rather than
 * asked for: a tenant is asked which country they operate in, and locale, currency and
 * region follow. Asking an SME owner to choose a BCP 47 tag is how you get `en-US` on
 * every row and discover it when the first invoice prints $ instead of ₹.
 *
 * Region is the load-bearing one. A D1 database's jurisdiction is fixed at creation, so
 * this mapping decides which database a tenant's data is written to for the life of the
 * account. It is derived on the server and never taken from the client.
 *
 * Data, not code — adding a market is an entry here plus a conformance test.
 * See docs/06-global-outcome-architecture.md §5 and §10.
 */

export type Region = "default" | "eu" | "in";

export interface CountryProfile {
  locale: string;
  currency: string;
  /**
   * IANA zones in this country, most populous first.
   *
   * A single entry means a contact's local hour is knowable from their phone number
   * alone. Several means it is not, and quiet-hours enforcement has to consider all of
   * them rather than pick one — see `localHoursFor`.
   */
  timezones: readonly string[];
  region: Region;
}

/** The EU/EEA set, which is a data-residency decision before it is a legal one. */
const EU_COUNTRIES: Record<string, { locale: string; timezones: readonly string[] }> = {
  DE: { locale: "de-DE", timezones: ["Europe/Berlin"] },
  FR: { locale: "fr-FR", timezones: ["Europe/Paris"] },
  ES: { locale: "es-ES", timezones: ["Europe/Madrid", "Atlantic/Canary"] },
  IT: { locale: "it-IT", timezones: ["Europe/Rome"] },
  NL: { locale: "nl-NL", timezones: ["Europe/Amsterdam"] },
  PL: { locale: "pl-PL", timezones: ["Europe/Warsaw"] },
  PT: { locale: "pt-PT", timezones: ["Europe/Lisbon", "Atlantic/Azores"] },
  IE: { locale: "en-IE", timezones: ["Europe/Dublin"] },
  BE: { locale: "nl-BE", timezones: ["Europe/Brussels"] },
  AT: { locale: "de-AT", timezones: ["Europe/Vienna"] },
  SE: { locale: "sv-SE", timezones: ["Europe/Stockholm"] },
  DK: { locale: "da-DK", timezones: ["Europe/Copenhagen"] },
  FI: { locale: "fi-FI", timezones: ["Europe/Helsinki"] },
};

const NON_EU: Record<string, CountryProfile> = {
  // Launch market one.
  IN: { locale: "en-IN", currency: "INR", timezones: ["Asia/Kolkata"], region: "in" },

  // WhatsApp-dense markets, launch phase two.
  ID: {
    locale: "id-ID",
    currency: "IDR",
    timezones: ["Asia/Jakarta", "Asia/Makassar", "Asia/Jayapura"],
    region: "default",
  },
  BR: {
    locale: "pt-BR",
    currency: "BRL",
    timezones: ["America/Sao_Paulo", "America/Manaus", "America/Rio_Branco"],
    region: "default",
  },
  MX: {
    locale: "es-MX",
    currency: "MXN",
    timezones: ["America/Mexico_City", "America/Chihuahua", "America/Tijuana"],
    region: "default",
  },
  AE: { locale: "ar-AE", currency: "AED", timezones: ["Asia/Dubai"], region: "default" },
  ZA: { locale: "en-ZA", currency: "ZAR", timezones: ["Africa/Johannesburg"], region: "default" },
  NG: { locale: "en-NG", currency: "NGN", timezones: ["Africa/Lagos"], region: "default" },
  PK: { locale: "en-PK", currency: "PKR", timezones: ["Asia/Karachi"], region: "default" },
  BD: { locale: "bn-BD", currency: "BDT", timezones: ["Asia/Dhaka"], region: "default" },
  PH: { locale: "en-PH", currency: "PHP", timezones: ["Asia/Manila"], region: "default" },
  MY: { locale: "ms-MY", currency: "MYR", timezones: ["Asia/Kuala_Lumpur"], region: "default" },
  SG: { locale: "en-SG", currency: "SGD", timezones: ["Asia/Singapore"], region: "default" },

  // Phase four.
  GB: { locale: "en-GB", currency: "GBP", timezones: ["Europe/London"], region: "default" },
  US: {
    locale: "en-US",
    currency: "USD",
    timezones: [
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Los_Angeles",
      "America/Anchorage",
      "Pacific/Honolulu",
    ],
    region: "default",
  },
  CA: {
    locale: "en-CA",
    currency: "CAD",
    timezones: [
      "America/Toronto",
      "America/Winnipeg",
      "America/Edmonton",
      "America/Vancouver",
      "America/Halifax",
      "America/St_Johns",
    ],
    region: "default",
  },
  AU: {
    locale: "en-AU",
    currency: "AUD",
    timezones: ["Australia/Sydney", "Australia/Adelaide", "Australia/Perth"],
    region: "default",
  },
};

export const COUNTRIES: Record<string, CountryProfile> = {
  ...NON_EU,
  ...Object.fromEntries(
    Object.entries(EU_COUNTRIES).map(([cc, v]) => [
      cc,
      { locale: v.locale, currency: "EUR", timezones: v.timezones, region: "eu" as const },
    ]),
  ),
};

/** Every country we can place. Sorted for a stable picker. */
export const SUPPORTED_COUNTRIES: readonly string[] = Object.keys(COUNTRIES).sort();

export function isSupportedCountry(country: string): boolean {
  return country.toUpperCase() in COUNTRIES;
}

/**
 * A country's profile, or a deliberately conservative default.
 *
 * The fallback is `default` region and USD rather than a guess at the nearest match: a
 * wrong region cannot be corrected later, so an unknown country must land somewhere it
 * was always going to be legal to put it.
 */
export function profileFor(country: string): CountryProfile {
  return (
    COUNTRIES[country.toUpperCase()] ?? {
      locale: "en-US",
      currency: "USD",
      timezones: ["UTC"],
      region: "default",
    }
  );
}

/** The database a tenant in this country is written to, for the life of the account. */
export function regionFor(country: string): Region {
  return profileFor(country).region;
}

/** Local wall-clock hour in one IANA zone. `Intl` handles DST; arithmetic on offsets does not. */
export function hourIn(timezone: string, at: number): number {
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      hour: "numeric",
      hour12: false,
    }).formatToParts(new Date(at));
    const hour = parts.find((p) => p.type === "hour")?.value;
    // "24" is how some engines render midnight under hour12:false.
    return hour === undefined ? new Date(at).getUTCHours() : Number(hour) % 24;
  } catch {
    return new Date(at).getUTCHours();
  }
}

/**
 * Every local hour it could plausibly be where this contact is.
 *
 * One entry when we know their zone, or when their country has only one. Several when we
 * know only the country and it spans zones — and then a quiet-hours rule has to hold
 * against *all* of them, because picking one means picking wrong for everybody else and
 * finding out through a regulator rather than a bug report.
 *
 * A contact with no country at all falls back to the tenant's own zone, which is the
 * best available proxy: most small businesses message people near them.
 */
export function localHoursFor(args: {
  country: string | null;
  timezone: string | null;
  fallbackTimezone: string;
  at: number;
}): number[] {
  if (args.timezone) return [hourIn(args.timezone, args.at)];

  const zones = args.country ? COUNTRIES[args.country.toUpperCase()]?.timezones : undefined;
  if (!zones || zones.length === 0) return [hourIn(args.fallbackTimezone, args.at)];

  return [...new Set(zones.map((z) => hourIn(z, args.at)))].sort((a, b) => a - b);
}
